import { useMemo } from "react";
import { UseFormRegister } from "react-hook-form";
import { GetAccount200ResponsePositionsInner, SideResponse } from "../../codegen-api";
import { FormValidatorKeysEnum } from "../../enums/form";
import { roundToStepSize } from "../../utils/format";
import { calculatePnl } from "../../utils/math";
import {
  closeSizeName,
  slLimitPriceName,
  slRoiName,
  slTriggerPriceName,
  tpLimitPriceName,
  tpRoiName,
  tpTriggerPriceName,
} from "./TPSLForPositionModal.helpers";

/**
 * The executor's own bound on a triggered close: a long is sold, so its limit
 * may sit at most 5% ABOVE the trigger; a short is bought back, so its limit
 * may sit at most 5% BELOW. Anything further and the close would never fill.
 */
export const LIMIT_PRICE_UPPER_MULTIPLIER = 1.05;
export const LIMIT_PRICE_LOWER_MULTIPLIER = 0.95;

/**
 * A close size, floored onto `amount_step`, is a real partial or full close:
 * at least one step and no more than the position. The floored value is what
 * the submit path signs (see `resolveCloseSize`), so validating the typed value
 * with a float slack let a size just past the position through.
 */
export function isCloseSizeWithinPosition(
  value: string | number,
  positionAmount: number,
  amountStep: number,
  amountPrecision: number
): boolean {
  const floored = roundToStepSize(value, amountStep, amountPrecision);
  return floored > 0 && floored <= positionAmount;
}

/** A trigger price is at least one `price_step` once snapped onto that step. */
export function isPriceAtLeastOneStep(
  value: string | number,
  priceStep: number,
  pricePrecision: number
): boolean {
  return roundToStepSize(value, priceStep, pricePrecision) >= Number(priceStep);
}

export function isLimitPriceWithinBound(
  limitPrice: number,
  triggerPrice: number,
  isLong: boolean
): boolean {
  if (!(limitPrice > 0) || !(triggerPrice > 0)) {
    return true;
  }
  return isLong
    ? limitPrice <= triggerPrice * LIMIT_PRICE_UPPER_MULTIPLIER
    : limitPrice >= triggerPrice * LIMIT_PRICE_LOWER_MULTIPLIER;
}

interface UseTPSLRegistersParams {
  position?: GetAccount200ResponsePositionsInner;
  actualTpTriggerPrice: any;
  actualSlTriggerPrice: any;
  loading: boolean;
  register: UseFormRegister<any>;
  triggerPriceValidateFn: (isTakeProfit: boolean) => any;
  contractPriceStep: any;
  /** When the checkbox is off the field is ignored, so it must not block. */
  configureAmount: boolean;
  useLimitPrice: boolean;
}

export function useTPSLRegisters({
  position,
  actualTpTriggerPrice,
  actualSlTriggerPrice,
  loading,
  register,
  triggerPriceValidateFn,
  contractPriceStep,
  configureAmount,
  useLimitPrice,
}: UseTPSLRegistersParams) {
  const isLong = position?.side === SideResponse.Buy;
  const positionAmount = Number(position?.amount) || 0;
  const takeProfitExpectedPnl = useMemo(() => {
    if (!position?.avg_entry_price || !actualTpTriggerPrice) {
      return undefined;
    }
    return (
      calculatePnl(
        Number(position.avg_entry_price) || 0,
        Number(actualTpTriggerPrice) || 0,
        position.side
      ) * Number(position.amount)
    );
  }, [actualTpTriggerPrice, position]);

  const stopLossExpectedPnl = useMemo(() => {
    if (!position?.avg_entry_price || !actualSlTriggerPrice) {
      return undefined;
    }
    return (
      calculatePnl(
        Number(position.avg_entry_price) || 0,
        Number(actualSlTriggerPrice) || 0,
        position.side
      ) * Number(position.amount)
    );
  }, [actualSlTriggerPrice, position]);

  const tpPriceRegister = useMemo(
    () =>
      register(tpTriggerPriceName, {
        disabled: loading,
        required: false,
        validate: {
          ...triggerPriceValidateFn(true),
          [FormValidatorKeysEnum.decimalsTooSmall]: (v) =>
            !v ||
            isPriceAtLeastOneStep(
              v,
              contractPriceStep.price_step,
              contractPriceStep.price_precision
            ),
        },
      }),
    [
      contractPriceStep.price_precision,
      contractPriceStep.price_step,
      loading,
      register,
      triggerPriceValidateFn,
    ]
  );

  const tpGainRegister = useMemo(
    () =>
      register(tpRoiName, {
        disabled: loading,
        required: false,
        validate: {
          [FormValidatorKeysEnum.roiTooHigh]: (v) =>
            position?.side !== SideResponse.Buy || !v || parseFloat(v) < 1000,
        },
      }),
    [loading, position?.side, register]
  );

  const slPriceRegister = useMemo(
    () =>
      register(slTriggerPriceName, {
        disabled: loading,
        required: false,
        validate: {
          ...triggerPriceValidateFn(false),
          [FormValidatorKeysEnum.decimalsTooSmall]: (v) =>
            !v ||
            isPriceAtLeastOneStep(
              v,
              contractPriceStep.price_step,
              contractPriceStep.price_precision
            ),
        },
      }),
    [
      contractPriceStep.price_precision,
      contractPriceStep.price_step,
      loading,
      register,
      triggerPriceValidateFn,
    ]
  );

  const slLossRegister = useMemo(
    () =>
      register(slRoiName, {
        disabled: loading,
        required: false,
        validate: {
          [FormValidatorKeysEnum.roiTooLow]: (v) =>
            position?.side === SideResponse.Buy || !v || parseFloat(v) > -1000,
        },
      }),
    [loading, position?.side, register]
  );

  const closeSizeRegister = useMemo(
    () =>
      register(closeSizeName, {
        disabled: loading,
        required: false,
        validate: {
          // Below one step floors to nothing, which is not a close at all.
          [FormValidatorKeysEnum.moreThanZero]: (v) =>
            !configureAmount ||
            roundToStepSize(
              v,
              contractPriceStep.amount_step,
              contractPriceStep.amount_precision
            ) > 0,
          [FormValidatorKeysEnum.reduceOnlyOrderSizeValid]: (v) =>
            !configureAmount ||
            !v ||
            isCloseSizeWithinPosition(
              v,
              positionAmount,
              contractPriceStep.amount_step,
              contractPriceStep.amount_precision
            ),
        },
      }),
    [
      configureAmount,
      contractPriceStep.amount_precision,
      contractPriceStep.amount_step,
      loading,
      positionAmount,
      register,
    ]
  );

  const tpLimitPriceRegister = useMemo(
    () =>
      register(tpLimitPriceName, {
        disabled: loading,
        required: false,
        validate: {
          [FormValidatorKeysEnum.limitPriceAboveTriggerPrice]: (v) =>
            !useLimitPrice ||
            !v ||
            isLimitPriceWithinBound(
              parseFloat(v),
              Number(actualTpTriggerPrice),
              isLong
            ),
        },
      }),
    [actualTpTriggerPrice, isLong, loading, register, useLimitPrice]
  );

  const slLimitPriceRegister = useMemo(
    () =>
      register(slLimitPriceName, {
        disabled: loading,
        required: false,
        validate: {
          [FormValidatorKeysEnum.limitPriceBelowTriggerPrice]: (v) =>
            !useLimitPrice ||
            !v ||
            isLimitPriceWithinBound(
              parseFloat(v),
              Number(actualSlTriggerPrice),
              isLong
            ),
        },
      }),
    [actualSlTriggerPrice, isLong, loading, register, useLimitPrice]
  );

  return {
    takeProfitExpectedPnl,
    stopLossExpectedPnl,
    tpPriceRegister,
    tpGainRegister,
    slPriceRegister,
    slLossRegister,
    closeSizeRegister,
    tpLimitPriceRegister,
    slLimitPriceRegister,
  };
}
